import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { toast } from "sonner";
import { useUploadCourseFileMutation } from "@/features/api/courseApi";
import { cn } from "@/utils/utils";

export default function PictureFillInTheBlanksExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";
  const [uploadFile] = useUploadCourseFileMutation();

  const completedRef = useRef(false);

  /* ---------------- RESET COMPLETION ---------------- */
  useEffect(() => {
    if (!showResults) completedRef.current = false;
  }, [showResults]);

  /* ---------------- SAFE QUESTIONS ---------------- */
  const questions = Array.isArray(exercise.pictureFillInTheBlanksQuestions)
    ? exercise.pictureFillInTheBlanksQuestions
    : [];

  /* ---------------- STUDENT STATE ---------------- */
  const [answers, setAnswers] = useState([]);

  /* ---------------- RESET ON RETRY ---------------- */
  useEffect(() => {
    if (!exercise?._id) return;

    const fresh = questions.map(q =>
      Array((q.sentence?.match(/_+/g) || []).length).fill("")
    );

    setAnswers(fresh);
    completedRef.current = false;
  }, [exercise._id]);

  /* ---------------- NORMALIZE ---------------- */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text || "").trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  /* ---------------- MEDIA ---------------- */
  const resolveMediaUrl = (url) => {
    if (!url) return "";
    const file = url.split("/").pop();
    return file ? `/uploads/image/${file}` : "";
  };

  /* ---------------- EDIT HELPERS ---------------- */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("pictureFillInTheBlanksQuestions", next);
  };

  const uploadImage = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await uploadFile(fd).unwrap();
      return res.fileUrl || res.url || "";
    } catch {
      toast.error("Image upload failed");
      return "";
    }
  };

  const updateQuestion = (qIdx, patch) => {
    const next = [...questions];
    next[qIdx] = { ...next[qIdx], ...patch };
    updateQuestions(next);
  };

  /* ---------------- INPUT ---------------- */
  const handleInputChange = (qIdx, bIdx, value) => {
    if (isEditMode || showResults) return;

    setAnswers(prev => {
      const next = [...prev];

      if (!next[qIdx]) {
        const blanks =
          (questions[qIdx]?.sentence?.match(/_+/g) || []).length;
        next[qIdx] = Array(blanks).fill("");
      }

      next[qIdx] = [...next[qIdx]];
      next[qIdx][bIdx] = value;

      // 🔥 ALWAYS notify parent (important for submit enable)
      onComplete(next, false);

      const allFilled = next.every(q => q.every(a => a.trim()));

      if (allFilled && !completedRef.current) {
        completedRef.current = true;

        const allCorrect = questions.every((q, qi) =>
          q.answers.every((ans, ai) =>
            normalize(next[qi]?.[ai], q.requireCapitalization) ===
            normalize(ans, q.requireCapitalization)
          )
        );

        // ✅ FINAL correct state
        onComplete(next, allCorrect);
      }

      return next;
    });
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => {
          const blanks = (q.sentence?.match(/_+/g) || []).length;

          return (
            <Card key={qIdx} className="p-6 space-y-6">
              <TextareaField
                label={`Sentence ${qIdx + 1} (use ___ for blanks)`}
                value={q.sentence || ""}
                onChange={(v) => {
                  const count = (v.match(/_+/g) || []).length;
                  updateQuestion(qIdx, {
                    sentence: v,
                    answers: Array.from(
                      { length: count },
                      (_, i) => q.answers?.[i] || ""
                    ),
                  });
                }}
                rows={4}
                required
              />

              <div className="space-y-3">
                <label className="font-medium">Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const url = await uploadImage(file);
                    if (url) updateQuestion(qIdx, { image: url });
                  }}
                />

                {q.image && (
                  <img
                    src={resolveMediaUrl(q.image)}
                    className="max-w-xs rounded-lg border"
                  />
                )}
              </div>

              <div className="space-y-3">
                <label className="font-medium">Correct Answers</label>
                {Array.from({ length: blanks }).map((_, i) => (
                  <StringField
                    key={i}
                    label={`Blank ${i + 1}`}
                    value={q.answers[i] || ""}
                    onChange={(v) => {
                      const next = [...q.answers];
                      next[i] = v;
                      updateQuestion(qIdx, { answers: next });
                    }}
                  />
                ))}
              </div>

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={(c) =>
                  updateQuestion(qIdx, { requireCapitalization: c })
                }
              />

              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  updateQuestions(
                    questions.filter((_, i) => i !== qIdx)
                  )
                }
              >
                Remove Question
              </Button>
            </Card>
          );
        })}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...questions,
              {
                sentence: "",
                answers: [],
                image: "",
                requireCapitalization: false,
              },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="text-base">
          Picture Fill in the Blanks
        </CardTitle>
        <CardDescription>
          Look at the image and complete the sentence.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-12">
        {questions.map((q, qIdx) => {
          const parts = q.sentence.split(/(_+)/g).filter(Boolean);
          const row = answers[qIdx] || [];
          let blankIndex = 0;

          return (
            <div key={qIdx} className="space-y-8">
              {q.image && (
                <div className="flex justify-center">
                  <img
                    src={resolveMediaUrl(q.image)}
                    className="max-h-80 rounded-xl border shadow"
                  />
                </div>
              )}

              <div className="p-8 bg-muted/40 rounded-xl text-lg flex flex-wrap justify-center items-center gap-3">
                {parts.map((part, i) => {
                  if (/_+/.test(part)) {
                    const bIdx = blankIndex++;

                    const correct =
                      normalize(row[bIdx], q.requireCapitalization) ===
                      normalize(q.answers[bIdx], q.requireCapitalization);

                    return (
                      <Input
                        key={i}
                        value={row[bIdx] || ""}
                        onChange={(e) =>
                          handleInputChange(qIdx, bIdx, e.target.value)
                        }
                        disabled={showResults}
                        className={cn(
                          "w-36 text-center font-semibold border transition",

                          // correct
                          showResults && correct &&
                            "bg-green-100 border-green-400",

                          // wrong
                          showResults && row[bIdx] && !correct &&
                            "bg-red-100 border-red-400",

                          showResults && "cursor-not-allowed"
                        )}
                      />
                    );
                  }

                  return <span key={i}>{part}</span>;
                })}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}